import { css, html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators";
import { SvgIcons } from "../../voyage-svg-icons/src/voyage-svg-icons";
import { ImminentRiskModel } from "../../../src/models/imminent-risk-model";
import { audioAlertStyles } from "../audio-styles";
import audioAlert from "../../../assets/audio/vessel-alert.mp3";
@customElement('onboard-audio-alert')
export class OnboardAudioAlert extends LitElement {
    @state() private _currentStep = 0;
    @state() private _isPlaying = false;
    @state() private _userHeardAudio: boolean | null = null;
    @state() private _hasPlayedOnce = false;

    connectedCallback(){
      super.connectedCallback();
      this._stopAudio();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this._stopAudio();
    }

    private _audio?: HTMLAudioElement;

    static get styles(){
        return [audioAlertStyles];
    }

    private _nextStep() {
        if (this._currentStep < this._steps.length - 1) this._currentStep++;
    }

    private _prevStep() {
        if (this._currentStep > 0) this._currentStep--;
    }

    private async _finish() {
        if (this._userHeardAudio === null) {
            return;
        }
        this.dispatchEvent(new Event('close'));
        await ImminentRiskModel!.finishOnboarding(this._userHeardAudio);  
    }

    private _playAudio() {
        if (this._isPlaying) {
            this._audio?.pause();
            this._audio!.currentTime = 0;
            this._isPlaying = false;
            return;
        }
        
        this._audio = new Audio(audioAlert);
        this._audio.volume = 70 / 100; // Set volume to 70%
        this._isPlaying = true;

        this._audio.play()
            .then(() => {this._hasPlayedOnce = true; console.log('Audio started')})
            .catch(err => {
            console.error('Error playing audio:', err);
            this._isPlaying = false;
            });

        // When audio finishes naturally, reset UI
        this._audio.addEventListener('ended', () => {
            this._isPlaying = false;
        });
    }

    private _stopAudio() {
      if (this._audio) {
        this._audio.pause();
        this._audio.currentTime = 0;
        this._isPlaying = false;
      }
    }

    private get _steps() {
    return [
      {
        icon: SvgIcons.audioAlert,
        content: html`
          <p>
            We’ve added audio notifications for urgent alerts only, so you’re instantly informed when it matters most.
            <br />
            To make sure you don’t miss a critical update, please follow the quick steps to enable sound.
          </p>
        `,
        footer: html`<mc-button appearance="primary" @click=${this._nextStep}>I have done this, Next</mc-button>`
      },
      {
        icon: html`<img src="./assets/images/sound-permissions.gif" class="sound-permissions" />`,
        content: html`
          <h3>Allow Browser Sound Permissions:</h3>
          <p>
            Your browser settings may be blocking the audio<br />
            alert. Please follow the steps below to enable it.<br />
            Click on <i>Site information icon → Settings → </i><br />
              <i>More settings and permissions → Sound → Allow</i>
          </p>
        `,
        footer: html`
          <mc-button appearance="neutral" @click=${this._prevStep}>Back</mc-button>
          <mc-button appearance="primary" @click=${this._nextStep}>I have done this, Next</mc-button>
        `
      },
      {
        icon: SvgIcons.speakerOn,
        content: html`
          <h3>Check System Volume & Mute:</h3>
          <p>Ensure your device volume is turned up and not muted.</p>
        `,
        footer: html`
          <mc-button appearance="neutral" @click=${this._prevStep}>Back</mc-button>
          <mc-button appearance="primary" @click=${this._nextStep}>I have done this, Next</mc-button>
        `
      },
      {
        icon: SvgIcons.radio,
        content: html`
          <h3>Verify Audio Output Device:</h3>
          <p>
            Make sure the correct output device (speakers/headphones) is selected and connected properly in system sound
            settings.
          </p>
        `,
        footer: html`
          <mc-button appearance="neutral" @click=${this._prevStep}>Back</mc-button>
          <mc-button appearance="primary" @click=${this._nextStep}>I have done this, Next</mc-button>
        `
      },
      {
        icon: SvgIcons.playButton,
        content: html`
          <h3>Test Alert Audio:</h3>
          <p>Tap “Play” to check if your system is playing audio.</p>
          <mc-button
            appearance="secondary"
            @click=${this._playAudio}
            ?disabled=${this._isPlaying}
          >
            ${this._isPlaying ? '⏸ Playing…' : '▶ Play'}
          </mc-button>
        `,
        footer: html`${this._isPlaying
        ? html`
            <mc-button appearance="neutral" @click=${() => this.showPageAccordingtoUserResponse(false)}>No</mc-button>
            <mc-button appearance="primary" @click=${() => this.showPageAccordingtoUserResponse(true)}>Yes, I can hear it</mc-button>
            `
        : html`
            <mc-button appearance="neutral" @click=${this._prevStep}>Back</mc-button>
            <mc-button appearance="primary" @click=${async () => {this.showPageAccordingtoUserResponse(true);}}>Yes, I can hear it</mc-button>
            `}
        `
      },
      {
        icon: SvgIcons.speakerOn,
        content: html`
            <div class="troubleshooting">
            <h3>Test Alert Audio | Troubleshooting guideline</h3>
            <p>As it looks like you can't hear the audio, please follow the steps below to troubleshoot.</p>
            <ul>
            <li>✔ Check System Volume & Mute: Ensure your device volume is turned up and not muted.</li>
            <li>✔ Verify Audio Output Device: Ensure the correct device (speakers/headphones) is connected.</li>
            <li>✔ Allow Sound Permissions: When prompted, click "Allow" on your browser's audio permision popup. <br /> If you missed it, follow this: <i>Browser Settings → Site Settings → Sound → Allow</i></li>
            <li>✔ Restart Browser or Device: Sometimes a reboot resolves output issues.</li>
            <li>✔ Disable ‘Do Not Disturb’ Mode: Ensure your system isn’t muting notifications.</li>
            <li>✔ Run the App’s Test Sound:
                <mc-button appearance="secondary" @click=${this._playAudio}>
                ${this._isPlaying ? '⏸ Pause' : '▶ Play'}
                </mc-button>
              </li>
            </ul>
            </div>
        `,
        footer: html`
            <mc-button appearance="primary" @click=${this._finish}>Got it</mc-button>
        `
      },
      {
        icon: SvgIcons.allCheck,
        content: html`
          <h3>You are all set!</h3>
          <p>Your system is ready now to get an audio alert.</p>
        `,
        footer: html`
          <mc-button appearance="primary" @click=${this._finish}>Got it</mc-button>
        `
      }
    ];
  }

  private showPageAccordingtoUserResponse(heard: boolean) {
      this._stopAudio();
      if (heard) {
          this._userHeardAudio = heard; 
          console.log('User CAN hear the audio.');
          this._currentStep = 6;
      } else {
          this._userHeardAudio = heard;
          console.log('User could NOT hear the audio.');
          this._currentStep = 5; //trouble shooting page
      }
  } 

    render(){
        const step = this._steps[this._currentStep];
        const isTroubleshooting = this._currentStep === 5;

        return html`
            <mc-modal
                ?open=${true}
                hiddenclose
                entercloseactiondisabled
                escapecloseactiondisabled
                height="auto"
                padding="none"
            >
            <div class="minimize">
                <button
                  class="minimize-button"
                  slot="trigger"
                  @click=${()=>{
                    this.dispatchEvent(new Event('minimized'))
                  }}
                >
                  <mc-icon icon="shrink"></mc-icon>
                </button>
            </div>
            <div class="content">
                <div class="header">
                ${!isTroubleshooting
                    ? html`<p>Introducing Audio Alert <span class="badge">New feature</span></p>`: nothing}
                </div>
                <div class="modal-content">
                ${!isTroubleshooting
                ? html`
                    <div class="left">
                    <span style="font-size: 48px;">${step.icon}</span>
                    </div>
                `
                : nothing}
                <div class="right">${step.content}</div>
                </div>
                <div class="footer">${step.footer}</div>
            </div>
            </mc-modal>
        `
    }
}